import type { TutorialStep } from "@/constants/tutorialMissions";

/**
 * チュートリアルミッションの「いまどの手順か」を、開いている画面から決める (純粋関数)。
 *
 * 手順は「画面 (path)」と「光らせる場所 (target)」を持つ。利用者が実際に画面を操作して
 * 次の画面へ移ったり、モーダルを開いたりすると、その手順の target が画面に現れるので、
 * そこまで自動で進める (「できた」を押させない)。戻ったときは前の手順に戻る。
 *
 * ブラウザ API に触れず isVisible を引数で受け取るのは、単体テストで差し替えるため。
 */

/** 手順の画面 (例: "/cards/world") が、いま開いている画面か。末尾の / は無視する */
export function stepPathMatches(path: string | null, pathname: string): boolean {
  if (path === null) return true;
  return normalize(path) === normalize(pathname);
}

function normalize(p: string): string {
  const trimmed = p.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

/** 手順に、画面から進み具合を判断できる手がかりがあるか (無ければ「次へ」で進める) */
export function isDetectableStep(step: TutorialStep): boolean {
  return step.path !== null || step.target !== null;
}

export interface ResolvedStep {
  index: number;
  /** この手順の画面にいるのに、光らせる場所が見つからない (宝箱が出ていない等) */
  targetMissing: boolean;
}

/**
 * いまの手順を決める。
 * - 実行できる手順: 画面が合っていて (null はどの画面でも)、光らせる場所が見えている
 *   (場所の無い手順は、画面を名指ししていれば実行できる扱い)
 * - この画面の手順: 画面を名指しした手順のうち、いま開いている画面のもの
 * 1. この画面の手順より先に、実行できる手順があればそのいちばん先 (モーダルを開いた・次の画面へ移った)
 * 2. 無ければ、この画面の最初の手順 (場所が見えなければ「場所が無い」として出す。
 *    例: メンバー画面で相関図のタブを見ている → 「相手を選ぶ」を、一覧を出す案内つきで出す)
 * 3. この画面の手順も無ければ、実行できるいちばん先の手順 (下のメニューを押す等)。それも無ければ最初の手順
 *
 * 手がかりの無い手順だけのミッション (ホーム画面に追加など) では null を返す
 * (呼び出し側が「次へ」「戻る」で選んだ手順を使う)。
 */
export function resolveStep(
  steps: readonly TutorialStep[],
  pathname: string,
  isVisible: (selector: string) => boolean,
): ResolvedStep | null {
  if (steps.length === 0 || !steps.some(isDetectableStep)) return null;

  let doable = -1;
  for (let i = steps.length - 1; i >= 0; i--) {
    const step = steps[i];
    if (!isDetectableStep(step) || !stepPathMatches(step.path, pathname)) continue;
    if (step.target !== null ? isVisible(step.target) : step.path !== null) {
      doable = i;
      break;
    }
  }

  const screenStep = steps.findIndex(
    (step) => step.path !== null && stepPathMatches(step.path, pathname),
  );

  if (screenStep !== -1 && doable < screenStep) {
    const step = steps[screenStep];
    return {
      index: screenStep,
      targetMissing: step.target !== null && !isVisible(step.target),
    };
  }
  if (doable !== -1) return { index: doable, targetMissing: false };

  const first = steps[0];
  return {
    index: 0,
    targetMissing: first.target !== null && !isVisible(first.target),
  };
}

/** 画面を開いただけでクリアになるミッションのうち、いまの画面に当たるもの */
export function matchPageVisit<K extends string>(
  table: readonly { path: string; key: K }[],
  pathname: string,
): K | null {
  const hit = table.find((row) => stepPathMatches(row.path, pathname));
  return hit ? hit.key : null;
}
